// QR generation. Uses qrcode-generator: a tiny, dependency-free encoder. We
// render the module matrix to our own SVG (crisp at any print size) and to a
// PNG via canvas, so we control styling and downloads.

import qrcode from 'qrcode-generator';

interface QrMatrix {
  count: number;
  isDark: (r: number, c: number) => boolean;
}

function build(text: string): QrMatrix {
  // Error correction 'M' balances density and scan robustness for a URL.
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  return { count: qr.getModuleCount(), isDark: (r, c) => qr.isDark(r, c) };
}

// Standalone, scalable SVG string with a 4-module quiet zone.
export function qrSvg(text: string, sizePx = 256): string {
  const { count, isDark } = build(text);
  const quiet = 4;
  const total = count + quiet * 2;
  const rects: string[] = [];
  for (let r = 0; r < count; r++) {
    for (let c = 0; c < count; c++) {
      if (isDark(r, c)) {
        rects.push(`<rect x="${c + quiet}" y="${r + quiet}" width="1" height="1"/>`);
      }
    }
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${sizePx}" height="${sizePx}" ` +
    `viewBox="0 0 ${total} ${total}" shape-rendering="crispEdges" role="img" ` +
    `aria-label="QR code">` +
    `<rect width="${total}" height="${total}" fill="#ffffff"/>` +
    `<g fill="#000000">${rects.join('')}</g></svg>`
  );
}

// PNG data URL rendered from the same matrix via canvas.
export function qrPngDataUrl(text: string, sizePx = 512): string {
  const { count, isDark } = build(text);
  const quiet = 4;
  const total = count + quiet * 2;
  const scale = Math.max(1, Math.floor(sizePx / total));
  const dim = total * scale;
  const canvas = document.createElement('canvas');
  canvas.width = dim;
  canvas.height = dim;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, dim, dim);
  ctx.fillStyle = '#000000';
  for (let r = 0; r < count; r++) {
    for (let c = 0; c < count; c++) {
      if (isDark(r, c)) {
        ctx.fillRect((c + quiet) * scale, (r + quiet) * scale, scale, scale);
      }
    }
  }
  return canvas.toDataURL('image/png');
}
